import React, { useState, useEffect, useContext, useRef, ReactNode } from "react";
import queryString from "query-string";
import { useHistory, Link } from "react-router-dom";

import { Grid, IconButton, Divider, Button, Fab, Hidden, Chip, Typography } from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import BusinessIcon from "@material-ui/icons/Business";
import CustomerDocumentIcon from "@material-ui/icons/FolderShared";
import CustomerSigninsIcon from "@material-ui/icons/Assessment";
import AddIcon from "@material-ui/icons/Add";
import PublishIcon from "@material-ui/icons/Publish";

import Pagination, { initialPage, initialPageSize, getOffset } from "../components/Pagination";
import { getCustomers, importCustomers, exportCustomers } from "../api/CustomerAPI";
import { exportCfdis } from "../api/CustomerCfdiAPI";
import { Customer, Customers, CustomersQueryParams, CustomerImportSummaryResponse, CustomerStatus, CustomerExportResponse } from "../model/Customer";
import { CustomerDocumentStatus } from "../model/CustomerDocument";
import { AppContext } from "../context/AppContext";
import translate from "../i18n/Translator";
import ValidatedInput from "../components/ValidatedInput";
import CustomerMenu from "./CustomerMenu";
import CustomersMenu from "./CustomersMenu";
import Gridable, { GridableColumn } from "../components/Gridable";
import Ellipsis from "../components/Ellipsis";
import CustomersImportSummaryPopupProps from "./CustomersImportSummaryPopupProps";
import { ErrorSnackbar, SuccessSnackbar, WarningSnackbar } from "../components/Snackbars";
import CustomBackdrop from "../components/CustomBackdrop";
import ExportPopup from "../components/ExportPopup";
import CustomerClassificationsPopup from "./CustomerClassificationsPopup";
import { CfdiQueryParams, CfdisExportResponse } from "../model/Cfdi";
import NumberFormat from "react-number-format";

export function CustomersGeneral() {
    return (
        <CustomersList variant="general" icon={<BusinessIcon />} />
    );
}

export function CustomersDocuments() {
    return (
        <CustomersList variant="documents" icon={<CustomerDocumentIcon />} />
    );
}

export function CustomersSignins() {
    return (
        <CustomersList variant="signins" icon={<CustomerSigninsIcon />} status="ENABLED" />
    );
}

interface CustomersListProps {
    variant: "general" | "documents" | "signins";
    icon: ReactNode;
    status?: string;
}

const signinsValues = ["yes", "no"];

function CustomersList(props: CustomersListProps) {
    const context = useContext(AppContext);
    const history = useHistory();
    const signinsLabels = signinsValues.map(v => translate(v) as string);
    const qs = queryString.parse(window.location.search);
    const showChecks = context.isGranted("CustomersUpdate");
    const showClassifications = false;
    const customerStatusLabels = CustomerStatus.map((value: string) => translate(`customers.status.${value}`) as string);
    const customerDocumentStatusLabels = CustomerDocumentStatus.map((value: string) => translate(`documents.status.${value}`) as string);

    const paramsFromQueryString = (): CustomersQueryParams => {
        return {
            "search": typeof qs["search"] === "string" ? qs["search"] as string : "",
            "status": props.status || (typeof qs["status"] === "string" ? qs["status"] as string : ""),
            "documents_status": typeof qs["documents_status"] === "string" ? qs["documents_status"] as string : "",
            "classification": typeof qs["classification"] === "string" ? qs["classification"] as string : "",
            "sort_by": props.variant === "signins" ? "USE" : (props.variant === "documents" ? "DOCUMENTS_STATUS" : undefined),
            "with_signins": typeof qs["with_signins"] === "string" ? qs["with_signins"] === "yes" : undefined
        } as CustomersQueryParams;
    };

    const [status, setStatus] = useState<string>("loading");
    const [data, setData] = useState<Customers>();
    const [classifications, setClassifications] = useState<string[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>();

    const [params, setParams] = useState<CustomersQueryParams>(paramsFromQueryString);
    const [workingParams, setWorkingParams] = useState<CustomersQueryParams>(paramsFromQueryString);

    const [customer, setCustomer] = useState<Customer>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const fileInput = useRef<HTMLInputElement>(null);
    const [gridAnchorEl, setGridAnchorEl] = useState<null | HTMLElement>(null);
    const [importResponse, setImportResponse] = useState<CustomerImportSummaryResponse>();
    const [exportResponse, setExportResponse] = useState<CustomerExportResponse>();
    const [exportCfdiResponse, setExportCfdiResponse] = useState<CfdisExportResponse>();
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();
    const [warning, setWarning] = useState<string>();
    const [exporting, setExporting] = useState(false);

    const [page, setPage] = useState<number>(initialPage);
    const [pageSize, setPageSize] = useState<number>(initialPageSize);

    const load = () => {
        let offset = getOffset(page, pageSize);
        setSelected([]);
        getCustomers(context.session!.tenant!.id, pageSize, offset, params).then((response) => {
            setClassifications(response.classifications || []);
            setData(response);
            setStatus("loaded");
        }).catch((error) => {
            setStatus(error.message);
        });
    };

    useEffect(load, [context.session, page, pageSize, params]);

    const onChangedPage = (page: number) => {
        setPage(page);
        setData(undefined);
    };

    const onChangedPageSize = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
        setData(undefined);
    };

    const onAppliedFilter = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let qs = queryString.parse(window.location.search);
        qs["search"] = workingParams.search;
        qs["status"] = workingParams.status;
        qs["documents_status"] = workingParams.documents_status;
        qs["classification"] = workingParams.classification;
        qs["page"] = "1";

        if (workingParams.with_signins === undefined) {
            delete qs["with_signins"];
        } else {
            qs["with_signins"] = workingParams.with_signins ? "yes" : "no";
        }

        let url = window.location.pathname + "?" + queryString.stringify(qs);
        history.push(url);

        setParams(workingParams);
    };

    const onFilterChanged = (name: string, value: string, inputRef: any) => {
        setWorkingParams({ ...workingParams, [name]: value });
    };

    const onSigninChanged = (name: string, value: string, inputRef: any) => {
        if (value === "" || value === "---") {
            const tmp = { ...workingParams };
            delete tmp.with_signins;
            setWorkingParams(tmp);
        } else {
            setWorkingParams({ ...workingParams, with_signins: value === "yes" });
        }
    };

    const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) {
            return;
        }

        setUploading(true);
        importCustomers(context.session!.tenant!.id, event.target.files[0]).then((response) => {
            load();
            setImportResponse(response);
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setUploading(false);
        });
    }

    const showDownloadCustomers = () => {
        setGridAnchorEl(null);
        setExporting(true);
        exportCustomers(context.session!.tenant!.id, params).then((response) => {
            if (response.url) {
                setExportResponse(response);
            } else {
                setSuccess(translate("customers.download.async", { "total": response.total }) as string);
            }
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setExporting(false);
        });
    }

    const openClassificationCustomers = () => {
        setGridAnchorEl(null);
        if (!data) return;

        if (selected.length === 0) {
            setWarning(translate("customers.select") as string);
        } else {
            const selectedCustomers = data.items.filter(customer => selected.indexOf(customer.id) >= 0);
            setSelectedCustomers(selectedCustomers);
        }
    }

    const onClassify = () => {
        setAnchorEl(null);
        if (!customer) return;

        setSelectedCustomers([customer]);
    }

    const openFileImport = () => {
        fileInput.current!.click();
        setGridAnchorEl(null);
    }

    const onGetBalance = () => {
        setAnchorEl(null);
        if (!customer) return;

        setExporting(true);
        exportCfdis(context.session!.tenant!.id, customer.id, {
            search: "",
            type: "I",
            payment_status: "pending"
        } as CfdiQueryParams).then((response) => {
            if (response.url) {
                setExportCfdiResponse(response);
            } else {
                setSuccess(translate("cfdis.download.async", { "total": response.total }) as string);
            }
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setExporting(false);
        });
    };

    const onCloseImportDialog = () => {
        setImportResponse(undefined);
        setGridAnchorEl(null);
        fileInput.current!.value = "";
    };

    const onClickedOptions = (customer: Customer) => (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setCustomer(customer);
    };

    const onClickedMore = (event: React.MouseEvent<HTMLElement>) => {
        setGridAnchorEl(event.currentTarget);
    };

    const handleSelection = (ids: string[], customers: Customer[]) => {
        setSelected(ids);
    };

    const onCloseOption = () => {
        setAnchorEl(null);
        setCustomer(undefined);
    };

    const onCloseSnackbars = () => {
        setSuccess(undefined);
        setError(undefined);
        setWarning(undefined);
    };

    const onSuccessClassification = (classifications: string[], updated: number) => {
        setGridAnchorEl(null);
        setSelectedCustomers(undefined);
        setClassifications(classifications);
        setSuccess(translate("customers.classifications.success", { "updated": updated }) as string);
        load();
    };

    const onCloseClassification = () => {
        setGridAnchorEl(null);
        setSelectedCustomers(undefined);
    };

    const classificationColumn = (customer: Customer) => {
        if (!customer.classifications || customer.classifications.length === 0) return "---";
        return customer.classifications.join(", ");
    };

    const columns = () => {
        const columns = [
            {
                title: translate("customers.name"),
                converter: (customer: Customer) => (
                    <>
                        <Ellipsis text={customer.name} lenght={100} /><br />
                        <Ellipsis text={`${customer.rfc} | ${translate(`customers.status.${customer.status}`)}`} lenght={0} secondary uppercased={false} />
                        {showClassifications && customer.classifications && (
                            <Grid container spacing={1}>
                                {(customer.classifications).map(classification => (
                                    <Grid item xs="auto">
                                        <Chip variant="outlined" size="small" label={<small>{classification}</small>}></Chip>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </>
                ),
                xs: true
            },
            props.variant === "general" ? {
                title: translate("customers.external_id") as string,
                converter: (customer) => customer.external_id || "---",
                xs: false,
                sm: 3,
                md: 3,
                lg: 2,
                xl: 1
            } : undefined,
            props.variant === "general" ? {
                title: translate("customers.tax_id") as string,
                converter: (customer) => customer.tax_id || "---",
                xs: false,
                sm: 3,
                md: 3,
                lg: 2,
                xl: 1
            } : undefined,
            props.variant === "documents" ? {
                title: translate("documents.status.title") as string,
                converter: (customer) => customer.documents ? translate(`documents.status.${customer.documents.status}`) as string : "---",
                xs: false,
                sm: 3,
                md: 3,
                lg: 2,
                xl: 1
            } : undefined,
            props.variant === "signins" ? undefined : {
                title: translate("customers.classifications.title") as string,
                converter: classificationColumn,
                xs: false,
                sm: false,
                md: false,
                lg: 2,
                xl: 2
            },
            props.variant === "signins" ? {
                title: (
                    <Typography variant="body2" component="h5" align="right">
                        <strong>
                            {translate("customers.all_signins")}
                        </strong>
                    </Typography>
                ),
                converter: (customer) => (
                    <Typography variant="body2" component="h5" align="right">
                        <NumberFormat value={customer.all_signins || 0} thousandSeparator displayType="text" />
                    </Typography>
                ),
                xs: false,
                sm: false,
                md: 2,
                lg: 2,
                xl: 2
            } : undefined,
            props.variant === "signins" ? {
                title: (
                    <Typography variant="body2" component="h5" align="right">
                        <strong>
                            {translate("customers.month_signins")}
                        </strong>
                    </Typography>
                ),
                converter: (customer) => (
                    <Typography variant="body2" component="h5" align="right">
                        <NumberFormat value={customer.signins || 0} thousandSeparator displayType="text" />
                    </Typography>
                ),
                xs: 3,
                sm: 3,
                md: 2,
                lg: 2,
                xl: 2
            } : undefined,
            props.variant === "signins" ? {
                title: (
                    <Typography variant="body2" component="h5" align="right">
                        <strong>
                            {translate("customers.invoices")}
                        </strong>
                    </Typography>
                ),
                converter: (customer) => (
                    <Typography variant="body2" component="h5" align="right">
                        <NumberFormat value={customer.invoices || 0} thousandSeparator displayType="text" />
                    </Typography>
                ),
                xs: false,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
            } : undefined,
            {
                title: (
                    <IconButton size="small" style={{ "visibility": "hidden" }} disabled><MoreVertIcon /></IconButton>
                ),
                converter: (customer) => (
                    <IconButton aria-label="options" color="default" size="small" onClick={onClickedOptions(customer)}
                        disabled={(selected.length === 1 && selected.indexOf(customer.id) < 0) || selected.length > 1}>
                        <MoreVertIcon />
                    </IconButton>
                ),
                justify: "flex-end",
                xs: "auto"
            }
        ] as GridableColumn<Customer>[];

        return columns.filter(column => column !== undefined);
    };

    return (
        <Pagination title={translate("customers.title")} subtitle={translate(`customers.${props.variant}`)} icon={props.icon}
            page={page} pageSize={pageSize} count={data ? data.items.length : 0} total={data ? data.total : 0}
            onChangedPage={onChangedPage} onChangedPageSize={onChangedPageSize} action={context.isGranted("CustomersCreate") ?
                (<Grid container alignItems="center" justify="flex-end" spacing={1}>
                    <Grid item xs="auto">
                        <Link to="/customers/new">
                            <Fab color="primary" size="small" title={translate("buttons.add") as string}>
                                <AddIcon />
                            </Fab>
                        </Link>
                    </Grid>
                    <Grid item xs="auto">
                        <Hidden xsDown implementation="css">
                            <Fab color="secondary" size="small" title={translate("buttons.import_customers") as string} onClick={openFileImport}>
                                <PublishIcon />
                            </Fab>
                        </Hidden>
                    </Grid>
                    <Grid item xs="auto">
                        <IconButton color="default" size="small" onClick={onClickedMore} disabled={uploading}>
                            <MoreVertIcon />
                        </IconButton>
                    </Grid>
                </Grid>)
                : undefined}>
            <form autoComplete="off" noValidate onSubmit={onAppliedFilter}>
                <Grid container alignItems="center" justify="flex-end" className="TableFilter" spacing={1}>
                    <Grid item xs={12} sm>
                        <ValidatedInput type="text" id="search" name="search" label={translate("customers.filter") as string}
                            margin="dense" disabled={false}
                            value={workingParams.search} onValueChanged={onFilterChanged} />
                    </Grid>
                    {(props.variant === "general" || props.variant === "signins") && (
                        <Grid item xs={12} sm={2} md={2} lg="auto">
                            <ValidatedInput type="text" id="status" name="status" label={translate("customers.status.title") as string}
                                margin="dense" disabled={false}
                                value={workingParams.status} onValueChanged={onFilterChanged}
                                options={CustomerStatus}
                                optionLabels={customerStatusLabels}
                                emptyOption={translate("any") as string} />
                        </Grid>
                    )}
                    {props.variant === "documents" && (
                        <Grid item xs={12} sm={2} md={2} lg="auto">
                            <ValidatedInput type="text" id="documents_status" name="documents_status" label={translate("documents.status.title") as string}
                                margin="dense" disabled={false}
                                value={workingParams.documents_status} onValueChanged={onFilterChanged}
                                options={CustomerDocumentStatus}
                                optionLabels={customerDocumentStatusLabels}
                                emptyOption={translate("any") as string} />
                        </Grid>
                    )}
                    {props.variant !== "signins" && (
                        <Grid item xs={12} sm={3} md={3} lg={2} xl={1}>
                            <ValidatedInput type="text" id="classification" name="classification" label={translate("customers.classifications.title") as string}
                                margin="dense" disabled={classifications.length === 0}
                                value={workingParams.classification} onValueChanged={onFilterChanged}
                                options={classifications}
                                emptyOption={translate("any") as string} />
                        </Grid>
                    )}
                    {props.variant === "signins" && (
                        <Grid item xs={12} sm={3} md={3} lg={2} xl={2}>
                            <ValidatedInput type="text" id="with_signins" name="with_signins" label={translate("customers.month_signins") as string}
                                margin="dense"
                                value={workingParams.with_signins === undefined ? "" : workingParams.with_signins ? "yes" : "no"} onValueChanged={onSigninChanged}
                                options={signinsValues} optionLabels={signinsLabels} />
                        </Grid>
                    )}
                    <Grid item xs="auto">
                        <Button type="submit" variant="outlined" color="secondary" size="medium">
                            {translate("buttons.search")}
                        </Button>
                    </Grid>
                </Grid>
            </form>
            <Divider />
            <Gridable
                items={data ? data.items : []}
                loading={status === "loading"}
                error={status !== "loading" && status !== "loaded" ? status : undefined}
                empty={translate("customers.empty") as string}
                selectable={showChecks}
                mapper={(customer) => customer.id}
                onChange={handleSelection}
                columns={columns()} />
            <input type="file" onChange={handleUpload} ref={fileInput} style={{ display: "none" }} accept=".xlsx, .csv, .xls" />
            <ErrorSnackbar message={error} onClose={onCloseSnackbars} />
            <SuccessSnackbar message={success} onClose={onCloseSnackbars} />
            <WarningSnackbar message={warning} onClose={onCloseSnackbars} />
            {customer && anchorEl &&
                <CustomerMenu customer={customer} anchor={anchorEl} onClassify={onClassify} onClose={onCloseOption} onGetBalance={onGetBalance} />
            }
            {importResponse && (
                <CustomersImportSummaryPopupProps data={importResponse} onClose={onCloseImportDialog} />
            )}
            {gridAnchorEl && (
                <CustomersMenu anchor={gridAnchorEl}
                    totalItems={data ? data.items.length : 0}
                    onClose={() => setGridAnchorEl(null)}
                    onImportCustomers={openFileImport}
                    onDownloadCustomers={showDownloadCustomers}
                    onClassify={openClassificationCustomers} />
            )}
            {exportResponse && exportResponse.url && (
                <ExportPopup
                    title={translate("customers.download.title") as string}
                    message={translate("customers.download.message", { "total": exportResponse.total }) as string}
                    url={exportResponse.url}
                    onClose={() => setExportResponse(undefined)} />
            )}
            {exportCfdiResponse && exportCfdiResponse.url && (
                <ExportPopup
                    title={translate("cfdis.download.title") as string}
                    message={translate("cfdis.download.message", { "total": exportCfdiResponse.total }) as string}
                    url={exportCfdiResponse.url}
                    onClose={() => setExportCfdiResponse(undefined)} />
            )}
            {selectedCustomers && (
                <CustomerClassificationsPopup
                    tenantId={context.session!.tenant!.id}
                    customers={selectedCustomers}
                    classifications={classifications}
                    onSuccess={onSuccessClassification}
                    onClose={onCloseClassification} />
            )}
            <CustomBackdrop open={uploading} message={translate("customers.import.loading") as string} />
            <CustomBackdrop open={exporting} />
        </Pagination>
    );
}
